<template>
	<V2Layout>
		<div class="v2-agn">

			<!-- Header -->
			<div class="v2-agn__header">
				<div class="v2-agn__header-left">
					<svg class="v2-agn__header-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
						<circle cx="9" cy="7" r="3.5" stroke="currentColor" stroke-width="1.5"/>
						<path d="M2 19C2 15.5 5 13 9 13C10.5 13 11.8 13.4 12.9 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
						<circle cx="17" cy="11" r="3" stroke="currentColor" stroke-width="1.5"/>
						<path d="M13 21C13 18 14.8 16 17 16C19.2 16 21 18 21 21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
					</svg>
					<h1 class="v2-agn__title">Agencies</h1>
					<span class="v2-agn__count">{{ totalCount }} total</span>
				</div>
				<div class="v2-agn__header-right">
					<button class="v2-agn__invite-btn" @click="openInviteModal">
						<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1V13M1 7H13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
						Invite Agency
					</button>
					<router-link :to="{ name: 'v2-agency-balance' }" class="v2-agn__balance-btn">
						<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1" y="3" width="12" height="9" rx="1.5" stroke="currentColor" stroke-width="1.3"/><path d="M1 6H13" stroke="currentColor" stroke-width="1.3"/><circle cx="10" cy="9" r="1" fill="currentColor"/></svg>
						Balance Management
					</router-link>
				</div>
			</div>

			<!-- Filter Bar -->
			<div class="v2-agn__filters">
				<div class="v2-agn__filter-right">
					<div class="v2-agn__search">
						<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="6" cy="6" r="4.5" stroke="currentColor" stroke-width="1.3"/><path d="M9.5 9.5L13 13" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
						<input
							v-model="searchQuery"
							type="text"
							placeholder="Search agencies..."
							class="v2-agn__search-input"
						/>
					</div>

					<div class="v2-agn__view-toggle">
						<button
							class="v2-agn__view-btn"
							:class="{ 'v2-agn__view-btn--active': viewMode === 'cards' }"
							@click="viewMode = 'cards'"
							title="Card view"
						>
							<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="1" y="1" width="6" height="6" rx="1" stroke="currentColor" stroke-width="1.3"/><rect x="9" y="1" width="6" height="6" rx="1" stroke="currentColor" stroke-width="1.3"/><rect x="1" y="9" width="6" height="6" rx="1" stroke="currentColor" fill="none" stroke-width="1.3"/><rect x="9" y="9" width="6" height="6" rx="1" stroke="currentColor" fill="none" stroke-width="1.3"/></svg>
						</button>
						<button
							class="v2-agn__view-btn"
							:class="{ 'v2-agn__view-btn--active': viewMode === 'table' }"
							@click="viewMode = 'table'"
							title="Table view"
						>
							<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 4H14M2 8H14M2 12H14" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
						</button>
					</div>
				</div>
			</div>

			<!-- Card View -->
			<div v-if="viewMode === 'cards'" class="v2-agn__cards">
				<!-- Agency Cards -->
				<div
					v-for="agency in filteredAgencies"
					:key="'a-' + agency.id"
					class="v2-agn-card v2-agn-card--clickable"
					@click="goToAgency(agency.id)"
				>
					<div class="v2-agn-card__top">
						<span class="v2-agn-card__avatar" :style="{ background: avatarColor(agency.name) }">{{ (agency.name || '?')[0].toUpperCase() }}</span>
						<div class="v2-agn-card__name-block">
							<span class="v2-agn-card__name">{{ agency.name }}</span>
						</div>
						<span class="v2-agn-card__status" :class="agency.is_deleted ? 'v2-agn-card__status--deleted' : 'v2-agn-card__status--active'">
							{{ agency.is_deleted ? 'Deleted' : 'Active' }}
						</span>
					</div>

					<div class="v2-agn-card__info">
						<div v-if="platformNames(agency)" class="v2-agn-card__row">
							<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><rect x="1" y="2" width="10" height="8" rx="1" stroke="currentColor" stroke-width="1.1"/><path d="M4 12H8" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/></svg>
							{{ platformNames(agency) }}
						</div>
					</div>

					<div class="v2-agn-card__footer">
						<span class="v2-agn-card__type">Agency</span>
						<button
							v-if="isSuperSSP"
							class="v2-agn-card__action-btn"
							:title="agency.is_deleted ? 'Restore agency' : 'Delete agency'"
							@click.stop="agency.is_deleted ? restoreAgency(agency) : removeAgency(agency)"
						>
							<svg v-if="agency.is_deleted" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 7C1 3.7 3.7 1 7 1C10.3 1 13 3.7 13 7C13 10.3 10.3 13 7 13C3.7 13 1 10.3 1 7Z" stroke="currentColor" stroke-width="1.3"/><path d="M4 7H10M7 4V10" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
							<svg v-else width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M4 4L10 10M10 4L4 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
						</button>
					</div>
				</div>

				<!-- Invitation Cards -->
				<div
					v-for="inv in filteredInvitations"
					:key="'i-' + inv.id"
					class="v2-agn-card v2-agn-card--invitation"
				>
					<div class="v2-agn-card__top">
						<span class="v2-agn-card__avatar v2-agn-card__avatar--invitation">
							<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="1.5" y="3.5" width="13" height="9" rx="1.5" stroke="currentColor" stroke-width="1.3"/><path d="M1.5 5L8 9.5L14.5 5" stroke="currentColor" stroke-width="1.3"/></svg>
						</span>
						<div class="v2-agn-card__name-block">
							<span class="v2-agn-card__name">{{ inv.email }}</span>
						</div>
						<span class="v2-agn-card__status v2-agn-card__status--invitation">Invitation</span>
					</div>

					<div class="v2-agn-card__info">
						<div class="v2-agn-card__row">
							<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5" stroke="currentColor" stroke-width="1.1"/><path d="M6 3V6L8 8" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/></svg>
							Expires {{ inv.expiration_date }}
						</div>
					</div>

					<div class="v2-agn-card__footer">
						<span class="v2-agn-card__type">Pending Invitation</span>
						<button
							class="v2-agn-card__action-btn"
							title="Delete invitation"
							@click.stop="removeInvitation(inv)"
						>
							<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M4 4L10 10M10 4L4 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
						</button>
					</div>
				</div>

				<div v-if="!filteredAgencies.length && !filteredInvitations.length" class="v2-agn__empty">
					<p>No agencies or invitations found</p>
					<p class="v2-agn__empty-sub">Try adjusting your search</p>
				</div>
			</div>

			<!-- Table View -->
			<div v-else class="v2-agn__table-wrap">
				<table class="nice-table">
					<thead>
						<tr>
							<th>Name&nbsp;&mdash;</th>
							<th>Platforms&nbsp;&mdash;</th>
							<th>Status&nbsp;&mdash;</th>
							<th>Expiration&nbsp;&mdash;</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						<tr
							v-for="agency in filteredAgencies"
							:key="'ta-' + agency.id"
							class="v2-agn__row v2-agn__row--clickable"
							@click="goToAgency(agency.id)"
						>
							<td class="v2-agn__row-name">{{ agency.name }}</td>
							<td>{{ platformNames(agency) }}</td>
							<td>
								<span class="v2-agn-card__status" :class="agency.is_deleted ? 'v2-agn-card__status--deleted' : 'v2-agn-card__status--active'">
									{{ agency.is_deleted ? 'Deleted' : 'Active' }}
								</span>
							</td>
							<td>&mdash;</td>
							<td class="v2-agn__row-actions">
								<button
									v-if="isSuperSSP"
									class="v2-agn-card__action-btn"
									:title="agency.is_deleted ? 'Restore' : 'Delete'"
									@click.stop="agency.is_deleted ? restoreAgency(agency) : removeAgency(agency)"
								>
									<svg v-if="agency.is_deleted" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 7C1 3.7 3.7 1 7 1C10.3 1 13 3.7 13 7C13 10.3 10.3 13 7 13C3.7 13 1 10.3 1 7Z" stroke="currentColor" stroke-width="1.3"/><path d="M4 7H10M7 4V10" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
									<svg v-else width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M4 4L10 10M10 4L4 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
								</button>
							</td>
						</tr>
						<tr
							v-for="inv in filteredInvitations"
							:key="'ti-' + inv.id"
							class="v2-agn__row"
						>
							<td class="v2-agn__row-name">{{ inv.email }}</td>
							<td>&mdash;</td>
							<td><span class="v2-agn-card__status v2-agn-card__status--invitation">Invitation</span></td>
							<td>{{ inv.expiration_date }}</td>
							<td class="v2-agn__row-actions">
								<button
									class="v2-agn-card__action-btn"
									title="Delete invitation"
									@click.stop="removeInvitation(inv)"
								>
									<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M4 4L10 10M10 4L4 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
								</button>
							</td>
						</tr>
						<tr v-if="!filteredAgencies.length && !filteredInvitations.length">
							<td colspan="5" class="v2-agn__empty-cell">No agencies or invitations found</td>
						</tr>
					</tbody>
				</table>
			</div>

			<!-- Invite Agency Modal -->
			<div v-if="showInviteModal" class="v2-agn-modal__overlay" @click.self="closeInviteModal">
				<div class="v2-agn-modal">
					<div class="v2-agn-modal__header">
						<h3 class="v2-agn-modal__title">Invite Agency</h3>
						<button class="v2-agn-modal__close" @click="closeInviteModal">
							<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 4L12 12M12 4L4 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
						</button>
					</div>
					<div class="v2-agn-modal__body">
						<label class="v2-agn-modal__label">Email address</label>
						<input
							ref="inviteEmailInput"
							v-model="inviteEmail"
							type="email"
							class="v2-agn-modal__input"
							placeholder="agency@example.com"
							@keyup.enter="sendInvitation"
						/>
						<p v-if="inviteSent" class="v2-agn-modal__success">Invitation sent successfully!</p>
						<p v-if="inviteError" class="v2-agn-modal__error">{{ inviteError }}</p>
					</div>
					<div class="v2-agn-modal__footer">
						<button class="v2-agn-modal__cancel" @click="closeInviteModal">Cancel</button>
						<button class="v2-agn-modal__send" :disabled="!inviteEmail || inviteSending" @click="sendInvitation">
							<template v-if="inviteSending">Sending...</template>
							<template v-else>Send Invitation</template>
						</button>
					</div>
				</div>
			</div>

			<router-view />
		</div>
	</V2Layout>
</template>


<script>
import { mapState } from 'vuex';
import V2Layout from '@/layouts/V2Layout';
import {
	requestChildAgencies,
	deleteChildAgency,
	restoreChildAgency,
} from '@/api/agency';
import {
	requestInvitations,
	createInvitation,
	deleteInvitation,
	INVITATION_ROLE_DSP_AGENCY,
} from '@/api/invitation';

export default {
	name: 'V2AgencyList',
	components: { V2Layout },

	data() {
		return {
			agencies: [],
			invitations: [],
			searchQuery: '',
			viewMode: localStorage.getItem('v2AgnViewMode') || 'cards',
			showInviteModal: false,
			inviteEmail: '',
			inviteSending: false,
			inviteSent: false,
			inviteError: '',
		};
	},

	async mounted() {
		await this.loadAll();
	},

	watch: {
		viewMode(val) { localStorage.setItem('v2AgnViewMode', val); },
	},

	computed: {
		...mapState('app', {
			currentAgencyId: 'agency',
		}),
		...mapState('profile', {
			isSuperSSP: 'is_super',
		}),

		totalCount() {
			return this.agencies.length + this.invitations.length;
		},

		filteredAgencies() {
			if (!this.searchQuery) return this.agencies;
			const q = this.searchQuery.toLowerCase();
			return this.agencies.filter(a => (a.name || '').toLowerCase().includes(q));
		},

		filteredInvitations() {
			if (!this.searchQuery) return this.invitations;
			const q = this.searchQuery.toLowerCase();
			return this.invitations.filter(i => (i.email || '').toLowerCase().includes(q));
		},
	},

	methods: {
		async loadAll() {
			const loading = this.$loading.show();
			try {
				const [agenciesList, invitationsList] = await Promise.all([
					requestChildAgencies(),
					requestInvitations(INVITATION_ROLE_DSP_AGENCY),
				]);
				this.agencies = agenciesList || [];
				this.invitations = invitationsList || [];
			} catch (err) {
				this.$log.error(err);
			} finally {
				loading.hide();
			}
		},

		platformNames(agency) {
			if (!agency.dsp_networks || !agency.dsp_networks.length) return '';
			return agency.dsp_networks.map(n => n.name).join(', ');
		},

		goToAgency(id) {
			this.$router.push({ name: 'v2-agency-details', params: { id } });
		},

		async removeAgency(agency) {
			if (!window.confirm(this.$t('users.confirm_agency_deletion'))) return;
			try {
				await deleteChildAgency(agency.id);
				await this.loadAll();
			} catch (err) {
				this.$log.error(err);
				window.alert(this.$t('users.failed_to_delete_agency'));
			}
		},

		async restoreAgency(agency) {
			if (!window.confirm(this.$t('users.confirm_agency_restoration'))) return;
			try {
				await restoreChildAgency(agency.id);
				await this.loadAll();
			} catch (err) {
				this.$log.error(err);
				window.alert(this.$t('users.failed_to_restore_agency'));
			}
		},

		async removeInvitation(inv) {
			if (!window.confirm(this.$t('invitation.confirm_invitation_deletion'))) return;
			try {
				await deleteInvitation(this.currentAgencyId, inv.id, INVITATION_ROLE_DSP_AGENCY);
				await this.loadAll();
			} catch (err) {
				this.$log.error(err);
				window.alert(this.$t('invitation.failed_to_delete_invitation'));
			}
		},

		openInviteModal() {
			this.inviteEmail = '';
			this.inviteSent = false;
			this.inviteError = '';
			this.inviteSending = false;
			this.showInviteModal = true;
			this.$nextTick(() => {
				if (this.$refs.inviteEmailInput) {
					this.$refs.inviteEmailInput.focus();
				}
			});
		},

		closeInviteModal() {
			this.showInviteModal = false;
		},

		async sendInvitation() {
			if (!this.inviteEmail || this.inviteSending) return;

			this.inviteSending = true;
			this.inviteSent = false;
			this.inviteError = '';

			try {
				const res = await createInvitation(
					this.currentAgencyId,
					this.inviteEmail,
					INVITATION_ROLE_DSP_AGENCY
				);

				if (!res) {
					throw new Error(this.$t('invitation.email_failed'));
				}

				this.inviteSent = true;
				this.inviteEmail = '';
				await this.loadAll();

				setTimeout(() => {
					this.closeInviteModal();
				}, 1500);
			} catch (error) {
				let message = this.$t('invitation.failed');
				if (error && error.response && error.response.data && error.response.data.error) {
					message = error.response.data.error;
				}
				this.inviteError = message;
			} finally {
				this.inviteSending = false;
			}
		},

		avatarColor(name) {
			const c = ['#2a9d8f', '#7c5ecf', '#2563eb', '#f97316', '#22c55e', '#ef4444', '#0891b2', '#eab308'];
			return c[((name || '').charCodeAt(0) || 0) % c.length];
		},
	},
};
</script>


<style lang="sass" scoped>
@import '~@/assets/styles/v2-tokens.sass'

.v2-agn
	font-family: $v2-font

// ── Header ──
.v2-agn__header
	display: flex
	align-items: center
	justify-content: space-between
	margin-bottom: 20px

.v2-agn__header-left
	display: flex
	align-items: center
	gap: 10px

.v2-agn__header-icon
	color: $v2-text-muted

.v2-agn__title
	font-size: 22px
	font-weight: 700
	margin: 0
	color: $v2-text

.v2-agn__count
	font-size: 12px
	color: $v2-text-muted
	background: #f3f4f6
	padding: 3px 10px
	border-radius: 20px

.v2-agn__header-right
	display: flex
	align-items: center
	gap: 10px

.v2-agn__invite-btn
	display: inline-flex
	align-items: center
	gap: 8px
	padding: 9px 20px
	background: transparent
	color: $v2-teal
	border: 1.5px solid $v2-teal
	border-radius: 10px
	font-family: $v2-font
	font-size: 13px
	font-weight: 600
	cursor: pointer
	transition: all 0.15s

	&:hover
		background: rgba($v2-teal, 0.06)

.v2-agn__balance-btn
	display: inline-flex
	align-items: center
	gap: 8px
	padding: 9px 20px
	background: $v2-teal
	color: #fff
	border: none
	border-radius: 10px
	font-family: $v2-font
	font-size: 13px
	font-weight: 600
	text-decoration: none
	transition: all 0.15s

	&:hover
		background: darken($v2-teal, 8%)

// ── Filters ──
.v2-agn__filters
	display: flex
	align-items: center
	justify-content: flex-end
	margin-bottom: 20px
	gap: 16px

.v2-agn__filter-right
	display: flex
	align-items: center
	gap: 10px

.v2-agn__search
	display: flex
	align-items: center
	gap: 8px
	padding: 7px 14px
	background: $v2-card-bg
	border: 1px solid $v2-border
	border-radius: 10px
	min-width: 220px
	color: $v2-text-muted

.v2-agn__search-input
	border: none !important
	outline: none !important
	background: transparent !important
	font-family: $v2-font
	font-size: 13px
	color: $v2-text
	width: 100%
	padding: 0 !important
	min-height: 0 !important
	box-shadow: none !important

	&::placeholder
		color: $v2-text-label

.v2-agn__view-toggle
	display: flex
	background: #f3f4f6
	border-radius: 8px
	padding: 2px

.v2-agn__view-btn
	display: flex
	align-items: center
	justify-content: center
	width: 32px
	height: 32px
	border: none
	background: transparent
	border-radius: 6px
	cursor: pointer
	color: $v2-text-muted
	transition: all 0.15s

	&:hover
		color: $v2-text

	&--active
		background: $v2-card-bg
		color: $v2-text
		box-shadow: 0 1px 3px rgba(0,0,0,0.08)

// ── Card Grid ──
.v2-agn__cards
	display: grid
	grid-template-columns: repeat(3, 1fr)
	gap: 14px

	@media (max-width: 1024px)
		grid-template-columns: repeat(2, 1fr)

	@media (max-width: 768px)
		grid-template-columns: 1fr

.v2-agn-card
	background: $v2-card-bg
	border: 1px solid $v2-border
	border-radius: $v2-radius-card
	padding: 18px 20px
	transition: all 0.2s
	display: flex
	flex-direction: column

	&--clickable
		cursor: pointer

		&:hover
			box-shadow: $v2-shadow-hover
			transform: translateY(-2px)

			.v2-agn-card__action-btn
				opacity: 1

	&--invitation
		border-style: dashed

.v2-agn-card__top
	display: flex
	align-items: flex-start
	gap: 10px
	margin-bottom: 14px

.v2-agn-card__avatar
	width: 36px
	height: 36px
	border-radius: 50%
	display: flex
	align-items: center
	justify-content: center
	color: #fff
	font-weight: 700
	font-size: 15px
	flex-shrink: 0

	&--invitation
		background: $v2-blue
		color: #fff

.v2-agn-card__name-block
	flex: 1
	min-width: 0

.v2-agn-card__name
	font-size: 14px
	font-weight: 600
	color: $v2-text
	display: block
	overflow: hidden
	text-overflow: ellipsis
	white-space: nowrap

.v2-agn-card__status
	font-size: 11px
	font-weight: 600
	padding: 3px 10px
	border-radius: 20px
	white-space: nowrap
	flex-shrink: 0

	&--active
		background: rgba($v2-green, 0.1)
		color: darken($v2-green, 10%)

	&--deleted
		background: rgba($v2-red, 0.1)
		color: darken($v2-red, 5%)

	&--invitation
		background: rgba($v2-blue, 0.1)
		color: darken($v2-blue, 5%)

.v2-agn-card__info
	display: flex
	flex-direction: column
	gap: 6px
	margin-bottom: 14px
	flex: 1

.v2-agn-card__row
	display: flex
	align-items: center
	gap: 8px
	font-size: 12px
	color: $v2-text-muted

	svg
		flex-shrink: 0
		color: $v2-text-label

.v2-agn-card__footer
	display: flex
	justify-content: space-between
	align-items: center
	padding-top: 12px
	border-top: 1px solid $v2-border

.v2-agn-card__type
	font-size: 11px
	color: $v2-text-label

.v2-agn-card__action-btn
	width: 28px
	height: 28px
	display: flex
	align-items: center
	justify-content: center
	border: 1px solid $v2-border
	border-radius: 8px
	background: $v2-card-bg
	cursor: pointer
	color: $v2-text-muted
	opacity: 0
	transition: all 0.15s

	.v2-agn-card:hover &,
	.v2-agn__row:hover &
		opacity: 1

	&:hover
		border-color: $v2-red
		color: $v2-red
		background: rgba($v2-red, 0.04)

// ── Table ──
.v2-agn__table-wrap
	overflow-x: auto

.v2-agn__row
	&--clickable
		cursor: pointer

.v2-agn__row-name
	font-weight: 600

.v2-agn__row-actions
	text-align: right

	.v2-agn-card__action-btn
		opacity: 0.3

.v2-agn__empty-cell
	text-align: center
	padding: 40px !important
	color: $v2-text-muted

.v2-agn__empty
	grid-column: 1 / -1
	text-align: center
	padding: 60px 20px
	color: $v2-text-muted

.v2-agn__empty-sub
	font-size: 13px
	color: $v2-text-label

// ── Invite Modal ──
.v2-agn-modal__overlay
	position: fixed
	top: 0
	left: 0
	right: 0
	bottom: 0
	background: rgba(0, 0, 0, 0.4)
	display: flex
	align-items: center
	justify-content: center
	z-index: 1000

.v2-agn-modal
	background: $v2-card-bg
	border-radius: $v2-radius-card
	width: 440px
	max-width: 90vw
	box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15)

.v2-agn-modal__header
	display: flex
	align-items: center
	justify-content: space-between
	padding: 20px 24px 0

.v2-agn-modal__title
	font-size: 18px
	font-weight: 700
	color: $v2-text
	margin: 0

.v2-agn-modal__close
	width: 32px
	height: 32px
	display: flex
	align-items: center
	justify-content: center
	border: none
	background: transparent
	border-radius: 8px
	cursor: pointer
	color: $v2-text-muted
	transition: all 0.15s

	&:hover
		background: #f3f4f6
		color: $v2-text

.v2-agn-modal__body
	padding: 20px 24px

.v2-agn-modal__label
	display: block
	font-size: 13px
	font-weight: 500
	color: $v2-text-muted
	margin-bottom: 8px

.v2-agn-modal__input
	width: 100%
	padding: 10px 14px
	border: 1.5px solid $v2-border
	border-radius: 10px
	font-family: $v2-font
	font-size: 14px
	color: $v2-text
	outline: none
	transition: border-color 0.15s
	box-sizing: border-box

	&:focus
		border-color: $v2-teal

	&::placeholder
		color: $v2-text-label

.v2-agn-modal__success
	margin-top: 10px
	font-size: 13px
	color: $v2-green
	font-weight: 500

.v2-agn-modal__error
	margin-top: 10px
	font-size: 13px
	color: $v2-red
	font-weight: 500

.v2-agn-modal__footer
	display: flex
	justify-content: flex-end
	gap: 10px
	padding: 0 24px 20px

.v2-agn-modal__cancel
	padding: 9px 20px
	background: transparent
	color: $v2-text-muted
	border: 1.5px solid $v2-border
	border-radius: 10px
	font-family: $v2-font
	font-size: 13px
	font-weight: 500
	cursor: pointer
	transition: all 0.15s

	&:hover
		border-color: $v2-text-muted
		color: $v2-text

.v2-agn-modal__send
	padding: 9px 20px
	background: $v2-teal
	color: #fff
	border: none
	border-radius: 10px
	font-family: $v2-font
	font-size: 13px
	font-weight: 600
	cursor: pointer
	transition: all 0.15s

	&:hover:not(:disabled)
		background: darken($v2-teal, 8%)

	&:disabled
		opacity: 0.5
		cursor: default
</style>
